import { forwardRef, type MouseEventHandler, type ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { CriticalIcon, PlayIcon } from '@dynatrace/strato-icons';

import { ProgressCircle } from '../../content/progress/ProgressCircle.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { Button } from '../button/Button.js';

const COMPONENT_NAME = 'RunQueryButton';

//#region Typings
/**
 * All options available for the RunQueryButton's state.
 * @public
 */
export const queryStateOptions = [
  'idle',
  'loading',
  'success',
  'error',
] as const;

/**
 * 1. idle: The query is yet to be run.
 * 2. loading: The query is currently running.
 * 3. success: The query can be rerun again (Query was successful and no errors have occurred).
 * 4. error: The query has returned an error, user can retry.
 * @public
 */
export type QueryStateType = (typeof queryStateOptions)[number];

/**
 * @public
 */
export interface RunQueryButtonProps
  extends
    AriaLabelingProps,
    DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /** When the run query button is disabled it cannot be interacted with.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * The current button state.
   * @defaultValue idle
   */
  queryState?: QueryStateType;

  /** Callback triggered when the RunQueryButton is interacted with. */
  onClick?: MouseEventHandler;

  /**
   * Whether the run query button is cancelable, i.e., if the loading
   * state can be interrupted.
   * @defaultValue true
   */
  cancelable?: boolean;
}
//#endregion

/**
 * @param queryState -
 * @returns the correct variant based on the state of the RunQueryButton
 */
function getVariant(
  queryState: QueryStateType,
): ['default', 'emphasized', 'accent'][number] {
  switch (queryState) {
    case 'idle':
      return 'accent';
    case 'loading':
    case 'success':
    case 'error':
      return 'emphasized';
  }
}

/**
 * @param queryState -
 * @returns the correct variant based on the state of the RunQueryButton
 */
function getColor(
  queryState: QueryStateType,
): ['primary', 'neutral', 'success', 'warning', 'critical'][number] {
  switch (queryState) {
    case 'idle':
      return 'primary';
    case 'loading':
    case 'success':
      return 'neutral';
    case 'error':
      return 'critical';
  }
}

/**
 * Component version of the correct prefix icon to be used in the
 * RunQueryButton based on its state.
 */
function PrefixIcon({
  type,
}: Readonly<{ type: QueryStateType }>): ReactElement | null {
  switch (type) {
    case 'idle':
    case 'success':
      return <PlayIcon />;
    case 'loading':
      return <ProgressCircle size="small" aria-hidden />;
    case 'error':
      return <CriticalIcon />;
  }
}

type RunQueryButtonMessageProps = {
  type: QueryStateType;
  cancelable: boolean;
};

/**
 * Component for the button label based on the query state.
 */
function ButtonMessage(
  props: Readonly<RunQueryButtonMessageProps>,
): ReactElement | null {
  const { type, cancelable } = props;

  switch (type) {
    case 'idle':
      return (
        <FormattedMessage
          description="Label for button used to run a query"
          defaultMessage="Run query"
          id="CwGi8xbTtV+fdsu5"
        />
      );
    case 'success':
      return (
        <FormattedMessage
          description="Label for button used to rerun a query after it was successful"
          defaultMessage="Run query"
          id="J9M12SSZNn2dKokI"
        />
      );
    case 'loading':
      return cancelable ? (
        <FormattedMessage
          description="Call to action for cancelable query button"
          defaultMessage="Cancel query"
          id="Za5bQfBDnyyur0mw"
        />
      ) : (
        <FormattedMessage
          description="Label for button indicating that a query is loading"
          defaultMessage="Running"
          id="hi3IodOo0ygpihhY"
        />
      );
    case 'error':
      return (
        <FormattedMessage
          description="Label for button indicating that an error has occurred while running the query. Click to retry."
          defaultMessage="Error (click to retry)"
          id="gg76gjIy5leuPbMT"
        />
      );
  }
}

/**
 * This button component is used for running queries. It
 * supports four query states: idle, loading, success, and error.
 * @public
 */
export const RunQueryButton = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  RunQueryButtonProps
>((props, ref) => {
  const {
    queryState = 'idle',
    disabled = false,
    onClick,
    cancelable = true,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: RunQueryButtonProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const allowsClickHandler =
    queryState !== 'loading' || (queryState === 'loading' && cancelable);

  return (
    <Button
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      onClick={allowsClickHandler ? onClick : undefined}
      color={getColor(queryState)}
      variant={getVariant(queryState)}
      ref={ref}
      disabled={disabled}
      aria-busy={queryState === 'loading'}
      aria-disabled={queryState === 'loading' && !cancelable}
      className={consumerClassName}
      style={consumerStyle}
      {...mergeProps(restProps, behavioralTrackingProps)}
      data-dt-component={COMPONENT_NAME}
    >
      <Button.Prefix>
        <PrefixIcon type={queryState} />
      </Button.Prefix>
      <span role="status">
        <ButtonMessage type={queryState} cancelable={cancelable} />
      </span>
    </Button>
  );
});

(
  RunQueryButton as typeof RunQueryButton & { displayName: string }
).displayName = COMPONENT_NAME;
